import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import type { AdvisorDoc, ClientAdvisorStatus } from '@agio/core/domain';
import { activeClientState } from '../../../state.js';
import { ErrorBanner } from '../../FormBits.js';
import { useSwitchClient } from '../../ClientPicker.js';
import { advisor } from './advisorApi.js';
import { ClientStatusTable } from './ClientStatusTable.js';
import { ClientSummaryPanel } from './ClientSummaryPanel.js';
import { UnsavedSwitchDialog } from './UnsavedSwitchDialog.js';
import { clientSwitchDecision, shouldWarnOnLeave } from './clientSwitch.js';

/**
 * Every client's state at a glance, then the client picked in the header
 * worked on below it. Choosing a row picks that client in the header too, so
 * the admin can walk the list and bring clients up to date one by one.
 *
 * The panel follows the header — except when the open draft has typing that
 * was never saved: then the admin is asked first (save / throw away / stay),
 * and leaving or reloading the page triggers the browser's own warning.
 */
export function ClientsTab() {
  const pickedClientId = useRecoilValue(activeClientState);
  const switchClient = useSwitchClient();
  // The client whose draft is open — normally the header's, until a switch is confirmed.
  const [shownClientId, setShownClientId] = useState(pickedClientId);
  // The open draft's unsaved typing (null when everything on screen is saved).
  const [unsaved, setUnsaved] = useState<AdvisorDoc | null>(null);
  // Bumped after every change in the panel, so the table reflects it.
  const [tick, setTick] = useState(0);
  const [statuses, setStatuses] = useState<ClientAdvisorStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nameOf = (id: string | null) => statuses?.find((s) => s.clientId === id)?.displayNameHe ?? id ?? '';

  useEffect(() => {
    let live = true;
    advisor.statuses().then((s) => live && setStatuses(s)).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [tick]);

  const decision = clientSwitchDecision(shownClientId, pickedClientId, unsaved !== null);
  useEffect(() => {
    if (decision !== 'follow') return;
    setShownClientId(pickedClientId);
    setUnsaved(null);
  }, [decision, pickedClientId]);

  const warnOnLeave = shouldWarnOnLeave(unsaved !== null);
  useEffect(() => {
    if (!warnOnLeave) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // older browsers need this to show their prompt
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [warnOnLeave]);

  return (
    <div className="grid grid-cols-1 gap-5">
      <ErrorBanner error={error} title="שגיאה" />
      <ClientStatusTable statuses={statuses} activeClientId={shownClientId} />
      {shownClientId && (
        <ClientSummaryPanel
          key={shownClientId}
          clientId={shownClientId}
          clientName={nameOf(shownClientId)}
          onUnsavedChange={setUnsaved}
          onChanged={() => setTick((t) => t + 1)}
        />
      )}
      {decision === 'ask' && shownClientId && unsaved && (
        <UnsavedSwitchDialog
          clientName={nameOf(shownClientId)}
          onSave={async () => {
            await advisor.saveDraft(shownClientId, unsaved);
            setTick((t) => t + 1);
            setUnsaved(null); // → the panel follows the header
          }}
          onDiscard={() => setUnsaved(null)}
          onStay={() => switchClient(shownClientId)}
        />
      )}
    </div>
  );
}
